// Per-lesson chat draft, kept in localStorage.
//
// WHY THIS EXISTS
//
// The Prompt Lab transcript lived only in React state, so a reload threw it away —
// and on the build-spec lesson that is the whole lesson: the student has spent
// twenty minutes telling Claude what their app does, and none of it is saved until
// they pass the grader. A refresh, a crash, a mis-swiped back gesture, or an
// expired-token failure that makes them reload all cost the same thing: everything.
//
// This is deliberately localStorage rather than a Firestore write per message. The
// case being fixed is "the page reloaded", which is same-browser by definition, and
// a server round trip on every turn would add latency and cost to the hot path of
// the one screen students spend the most time on. The transcript still reaches the
// server at the points that already write: grading and download.
//
// Drafts are namespaced by uid as well as lesson, because a shared computer is a
// real case in this course — without the uid the next student to open the lesson
// would be handed someone else's conversation.

const PREFIX = "promptLabDraft";
const VERSION = 1;

// Drafts outlive the tab on purpose, but not forever: a transcript from months ago
// is not something a student expects to see reappear, and dead keys accumulate on
// shared machines. Anything older is dropped on read and swept on write.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const keyFor = (uid, lessonID) => `${PREFIX}:${uid}:${lessonID}`;

// Private-mode Safari and storage-disabled browsers throw on plain access, so every
// entry point is guarded. Losing a draft is a downgrade to the old behaviour; a
// thrown error here would take the whole lesson down with it.
const storage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
        return null;
    }
};

const isDraftKey = (k) => typeof k === "string" && k.startsWith(`${PREFIX}:`);

/** Drop every draft that has aged out, plus (optionally) one specific key. */
const sweep = (store, alsoDrop = null) => {
    const doomed = [];
    for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (!isDraftKey(k)) continue;
        if (k === alsoDrop) {
            doomed.push(k);
            continue;
        }
        try {
            const savedAt = JSON.parse(store.getItem(k))?.savedAt;
            if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) doomed.push(k);
        } catch {
            doomed.push(k); // unparseable — it can't be restored anyway
        }
    }
    doomed.forEach((k) => {
        try {
            store.removeItem(k);
        } catch {
            /* nothing useful to do */
        }
    });
};

/**
 * The saved transcript for this student on this lesson, or [] if there isn't one.
 *
 * @param {string} uid
 * @param {string} lessonID
 * @returns {Array} the messages array, always safe to spread into state
 */
export const loadDraft = (uid, lessonID) => {
    const store = storage();
    if (!store || !uid || !lessonID) return [];
    try {
        const raw = store.getItem(keyFor(uid, lessonID));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (parsed?.v !== VERSION || !Array.isArray(parsed.messages)) return [];
        if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
            store.removeItem(keyFor(uid, lessonID));
            return [];
        }
        // Only the shape the chat renders. A hand-edited localStorage entry should
        // not be able to put arbitrary objects into the message list.
        return parsed.messages.filter(
            (m) => m && typeof m.role === "string" && typeof m.content === "string"
        );
    } catch {
        return [];
    }
};

/**
 * Persist the transcript. Writing an empty array clears the draft instead, so a
 * lesson that mounts before its restore lands can never blank out a saved one.
 *
 * @param {string} uid
 * @param {string} lessonID
 * @param {Array} messages
 */
export const saveDraft = (uid, lessonID, messages) => {
    const store = storage();
    if (!store || !uid || !lessonID) return;
    if (!Array.isArray(messages) || messages.length === 0) {
        clearDraft(uid, lessonID);
        return;
    }
    const payload = JSON.stringify({
        v: VERSION,
        savedAt: Date.now(),
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
    });
    try {
        store.setItem(keyFor(uid, lessonID), payload);
    } catch {
        // Out of quota — most likely someone else's stale drafts. Clear them and
        // take one more shot; if it still fails, carry on without a draft.
        try {
            sweep(store);
            store.setItem(keyFor(uid, lessonID), payload);
        } catch {
            /* draft unavailable; the lesson still works */
        }
    }
};

/** Forget this lesson's draft — called once the student has passed it. */
export const clearDraft = (uid, lessonID) => {
    const store = storage();
    if (!store || !uid || !lessonID) return;
    try {
        sweep(store, keyFor(uid, lessonID));
    } catch {
        /* nothing useful to do */
    }
};
